import { HTTP_INTERCEPTORS } from '@angular/common/http'
import { NgModule } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { BrowserModule, Title } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { RouterModule, Routes } from '@angular/router'
import { HeliosAgGridConfigModule } from '@helios/ag-grid-config'
import { HeliosAppContainerModule } from '@helios/app-container'
import { HeliosAuthModule } from '@helios/auth'
import { CommandsModule } from '@helios/commands'
import { CommentsModule } from '@helios/comments'
import { CopilotModule } from '@helios/copilot'
import { ErrorLogModule } from '@helios/error-log'
import { GenericComponentsModule } from '@helios/generic-components'
import { HotkeysModule } from '@helios/hotkeys'
import { LayoutManagerModule } from '@helios/layout-manager'
import { MessagingModule } from '@helios/messaging'
import { PanelCatalogModule } from '@helios/panel-catalog'
import { SessionsModule } from '@helios/sessions'
import { StateManagementModule } from '@helios/state-management'
import { TelemetryModule } from '@helios/telemetry'
import {
  UserPreferencesModule,
  UserPreferencesService,
} from '@helios/user-preferences'
import {
  NgbDatepickerModule,
  NgbDropdownModule,
  NgbModalConfig,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap'
import { NgSelectModule } from '@ng-select/ng-select'
import { EffectsModule } from '@ngrx/effects'
import { StoreModule } from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { MockNgConfigModule } from '@northfork/mock-ng-config'
import {
  AppContainerModule,
  ButtonModule,
  NfFormsModule,
  ToastModule,
} from '@northfork/ng-basics'
import { PanelModule } from '@northfork/ng-panel'
import { environment } from 'environments/environment'
import { GlobalConfig, ToastrModule } from 'ngx-toastr'
import { AdBlockerComponent } from './ad-blocker/ad-blocker.component'
import { AppComponent } from './app.component'
import { BreakFocusEffects } from './break-focus/breaks.effects'
import { MessagingStatusFooterComponent } from './components/messaging-status-footer/messaging-status-footer.component'
import { ExternalFileEffects } from './external-file-focus/external-file.effects'
import { PanelModules } from './helios-panels'
import { CommandsLoadingInterceptor } from './interceptors/commands-loading.interceptor'
import { DefaultHttpHeadersInterceptor } from './interceptors/default-http-headers.interceptor'
import { ErrorInterceptor } from './interceptors/error.interceptor'
import { SessionInterceptor } from './interceptors/session.interceptor'
import { JobsEffects } from './jobs-focus/jobs.effects'
import { LoginErrorComponent } from './login-error/login-error.component'
import { NewUserSetupComponent } from './new-user-setup/new-user-setup.component'
import { NotificationsBellComponent } from './notifications-bell/notifications-bell.component'
import { paths } from './paths'
import { ReloadPageComponent } from './reload-page/reload-page.component'
import { RouterComponent } from './router.component'
import { AvailableActionsEffects } from './available-actions/available-actions.effects'
import { SecurityMappingEffects } from './security-mapping-focus/security-mapping.effects'
import { HeliosMessagingService } from './services/helios-messaging/helios-messaging.service'
import { TransactionFocusEffects } from './transaction-focus/transactions.effects'

/**
 * This makes it so that by default, production users won't have
 * Redux Devtools. Devs can enable this by their user preferences
 * if they want it
 */
const devTools = []
if (!environment.production || UserPreferencesService.getReduxDevtools()) {
  devTools.push(StoreDevtoolsModule.instrument({ maxAge: 50 }))
}

export const routes: Routes = [
  {
    path: paths.base,
    component: AppComponent,
  },
  {
    path: paths.loginError,
    component: LoginErrorComponent,
  },
  {
    path: paths.session,
    component: AppComponent,
  },
  {
    path: paths.adBlocker,
    component: AdBlockerComponent,
  },
  {
    path: paths.inactive,
    component: ReloadPageComponent,
  },
  {
    path: paths.newUser,
    component: NewUserSetupComponent,
  },
  {
    path: '**',
    redirectTo: paths.base,
  },
]

const DEFAULT_TOASTR_CONFIG: Partial<GlobalConfig> = {
  positionClass: 'toast-bottom-right',
  preventDuplicates: true,
  maxOpened: 5,
  tapToDismiss: true,
  closeButton: true,
}

@NgModule({
  declarations: [
    AppComponent,
    RouterComponent,
    LoginErrorComponent,
    AdBlockerComponent,
    ReloadPageComponent,
    MessagingStatusFooterComponent,
    NewUserSetupComponent,
    NotificationsBellComponent,
  ],
  imports: [
    // Angular Modules
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    RouterModule.forRoot(routes),

    // Northfork Modules
    AppContainerModule,
    ButtonModule,
    MockNgConfigModule,
    NfFormsModule,
    PanelModule,
    ToastModule,
    ToastrModule.forRoot(DEFAULT_TOASTR_CONFIG),

    // Third Party Modules
    NgbDatepickerModule,
    NgbDropdownModule,
    NgSelectModule,
    NgbTooltipModule,

    // Helios Library Modules
    PanelCatalogModule,
    CommandsModule,
    LayoutManagerModule,
    HeliosAgGridConfigModule,
    TelemetryModule,
    ErrorLogModule,
    MessagingModule,
    // UserPreferencesModule has to go before HeliosAuthModule. This has to do with ordering providers for APP_INITIALIZER
    UserPreferencesModule,
    HotkeysModule.forRoot(),
    PanelCatalogModule,
    SessionsModule,
    HeliosAuthModule,
    HeliosAppContainerModule,
    GenericComponentsModule,

    // GenAI
    CopilotModule,
    CommentsModule,

    /**
     * REDUX IMPORTS
     */

    /**
     * There won't be any root reducers. All reducers will be put into its own feature slice.
     */
    StoreModule.forRoot(
      {},
      {
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      },
    ),

    /**
     * No root effects, each will be in a feature slice
     */
    EffectsModule.forRoot([
      AvailableActionsEffects,
      BreakFocusEffects,
      TransactionFocusEffects,
      ExternalFileEffects,
      SecurityMappingEffects,
      JobsEffects,
    ]),
    StateManagementModule,
    ...devTools,

    // App Modules
    HotkeysModule.forRoot(),
    ...PanelModules,
  ],

  providers: [
    Title,
    { provide: HTTP_INTERCEPTORS, useClass: SessionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CommandsLoadingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DefaultHttpHeadersInterceptor,
      multi: true,
    },
    NgbModalConfig,
    HeliosMessagingService,
  ],

  bootstrap: [RouterComponent],
})
export class AppModule {
  constructor(globalModalConfig: NgbModalConfig) {
    globalModalConfig.backdrop = false
    globalModalConfig.windowClass = 'hs-modal-window'
  }
}
